// quality-gate-exempt: pre-modularization — dense JSX with no good split point

/**
 * BrowseTab — Browse tab content for the PropBrowserModal.
 * Renders the game-grouped card layout (rebrand) and the legacy flat list fallback.
 */

import { BestLineBadge } from '@components/ui/BestLineBadge';
import { buildComparisonKey } from '@hooks/useOddsComparison';
import { isTeamBet } from '@parlay-picker/shared/correlationUtils';
import type { LeagueKey } from '@parlay-picker/shared/league';
import type { BestOddsResult } from '@parlay-picker/shared/oddsUtils';
import { getTeamAbbr, getTeamLogo } from '@parlay-picker/shared/teams';
import { cn } from '@utils/cn';
import type { BettableProp, Game } from '@utils/propUtils';
import { formatPropType, getBetDisplayName, getBetDisplayProp } from '@utils/propUtils';
import { getPlayerHeadshotUrl } from '@views/picks/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, ChevronDown, Plus } from 'lucide-react';
import type { ReactElement } from 'react';

import type { GameGroup } from './propBrowserTypes';

export interface BrowseTabProps {
  /** The league every game in `games` was loaded for. */
  leagueKey: LeagueKey;
  loading: boolean;
  error: string | null;
  games: Game[];
  availableProps: BettableProp[];
  filteredProps: BettableProp[];
  groupedProps: GameGroup[] | null;
  collapsedGames: Set<string>;
  toggleGameCollapsed: (gameId: string) => void;
  expandedPlayers: Set<string>;
  togglePlayer: (playerKey: string) => void;
  checkBlocked: (prop: BettableProp) => { canAdd: boolean; reason?: string };
  handleAddProp: (prop: BettableProp) => void;
  fetchGamesAndProps: () => Promise<void>;
  oddsComparisons: Map<string, BestOddsResult>;
  oddsLocked: boolean;
  setComparisonModalData: (data: BestOddsResult | null) => void;
  setComparisonSourceProp: (prop: BettableProp | null) => void;
}

export function BrowseTab({
  leagueKey,
  loading,
  error,
  games,
  availableProps,
  filteredProps,
  groupedProps,
  collapsedGames,
  toggleGameCollapsed,
  expandedPlayers,
  togglePlayer,
  checkBlocked,
  handleAddProp,
  fetchGamesAndProps,
  oddsComparisons,
  oddsLocked,
  setComparisonModalData,
  setComparisonSourceProp,
}: BrowseTabProps): ReactElement {
  if (loading) {
    return (
      <div className={'text-center py-12 text-theme-text-tertiary'}>
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-t-transparent mb-3 border-theme-primary" />
        <p>Loading available props...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-400">
        <p>{error}</p>
        <button
          type="button"
          onClick={fetchGamesAndProps}
          className="mt-3 px-4 py-2 rounded-lg text-sm text-white bg-theme-surface hover:bg-theme-surface-hover"
        >
          Retry
        </button>
      </div>
    );
  }

  if (games.length === 0) {
    return (
      <div className={'text-center py-12 text-theme-text-tertiary'}>
        <p className="text-lg mb-2">No games scheduled</p>
        <p className="text-sm">
          Props are only available when games are scheduled in the next 24 hours
        </p>
      </div>
    );
  }

  if (availableProps.length === 0) {
    return (
      <div className={'text-center py-12 text-theme-text-tertiary'}>
        <p className="text-lg mb-2">No props available yet</p>
        <p className="text-sm">Props typically become available closer to game time</p>
      </div>
    );
  }

  if (filteredProps.length === 0) {
    return (
      <div className={'text-center py-12 text-theme-text-tertiary'}>
        <p className="text-lg mb-2">No matching props</p>
        <p className="text-sm">Try adjusting your search or filters</p>
      </div>
    );
  }

  if (groupedProps) {
    return (
      /* Rebrand Mode: Game-grouped card layout */
      <div className="space-y-4">
        {groupedProps.map((group, groupIndex) => {
          const { game, playerGroups, gameProps } = group;
          const isCollapsed = collapsedGames.has(game.id);
          const totalProps =
            playerGroups.reduce((sum, pg) => sum + pg.props.length, 0) + gameProps.length;
          const awayAbbr = getTeamAbbr(game.away_team, game.sport_key);
          const homeAbbr = getTeamAbbr(game.home_team, game.sport_key);
          const gameTime = game.commence_time
            ? new Date(game.commence_time).toLocaleString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
              })
            : '';

          // Show date header when day changes between consecutive games
          const currentDate = game.commence_time
            ? new Date(game.commence_time).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'short',
                day: 'numeric',
              })
            : '';
          const prevCommenceTime =
            groupIndex > 0 ? groupedProps[groupIndex - 1].game.commence_time : undefined;
          const prevDate = prevCommenceTime
            ? new Date(prevCommenceTime).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'short',
                day: 'numeric',
              })
            : '';
          const showDateHeader = currentDate && currentDate !== prevDate;

          return (
            <div key={game.id}>
              {showDateHeader && (
                <div
                  className={cn(
                    'flex items-center gap-3 text-xs font-medium text-theme-text-tertiary uppercase tracking-wider',
                    groupIndex > 0 ? 'mt-6 mb-2' : 'mb-2',
                  )}
                >
                  <div className="h-px flex-1 bg-theme-border/40" />
                  <span>{currentDate}</span>
                  <div className="h-px flex-1 bg-theme-border/40" />
                </div>
              )}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: groupIndex * 0.05, duration: 0.3 }}
              >
                {/* Game Section Header */}
                <button
                  type="button"
                  onClick={() => toggleGameCollapsed(game.id)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-3 bg-theme-bg/70 border border-theme-border/30 rounded-xl hover:bg-theme-surface/50 transition cursor-pointer"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <img
                      src={getTeamLogo(game.away_team, game.sport_key)}
                      alt={awayAbbr}
                      className="w-5 h-5 shrink-0"
                      loading="lazy"
                    />
                    <span className="text-sm font-semibold text-white">{awayAbbr}</span>
                    <span className="text-xs text-theme-text-tertiary">@</span>
                    <span className="text-sm font-semibold text-white">{homeAbbr}</span>
                    <img
                      src={getTeamLogo(game.home_team, game.sport_key)}
                      alt={homeAbbr}
                      className="w-5 h-5 shrink-0"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {gameTime && (
                      <span className="text-xs text-theme-text-tertiary">{gameTime}</span>
                    )}
                    <span className="text-[10px] bg-theme-surface text-theme-text-tertiary px-1.5 py-0.5 rounded">
                      {totalProps} props
                    </span>
                    <motion.div
                      animate={{ rotate: isCollapsed ? 0 : 180 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown className="w-4 h-4 text-theme-text-tertiary" />
                    </motion.div>
                  </div>
                </button>

                {/* Collapsible content with animation */}
                <AnimatePresence initial={false}>
                  {!isCollapsed && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="mt-2 space-y-2 pl-2">
                        {/* Player Prop Cards */}
                        {playerGroups.map((pg) => {
                          const teamAbbr = getTeamAbbr(pg.team, game.sport_key);
                          const playerKey = `${game.id}:${pg.playerName}`;
                          const isPlayerExpanded = expandedPlayers.has(playerKey);
                          return (
                            <div
                              key={pg.playerName}
                              className="bg-theme-surface/30 border border-theme-border/20 rounded-xl overflow-hidden"
                            >
                              {/* Player header - clickable to expand/collapse */}
                              <button
                                type="button"
                                onClick={() => togglePlayer(playerKey)}
                                className="w-full flex items-center gap-3 p-3 hover:bg-theme-surface/50 transition cursor-pointer"
                              >
                                <img
                                  src={getPlayerHeadshotUrl(pg.playerName, leagueKey) ?? undefined}
                                  alt={pg.playerName}
                                  className="w-8 h-8 rounded-full object-cover bg-theme-surface shrink-0"
                                  loading="lazy"
                                />
                                <div className="flex-1 min-w-0 text-left">
                                  <span className="text-sm font-semibold text-white">
                                    {pg.playerName}
                                  </span>
                                  {teamAbbr && (
                                    <span className="ml-2 text-[10px] bg-theme-surface text-theme-text-tertiary px-1.5 py-0.5 rounded">
                                      {teamAbbr}
                                    </span>
                                  )}
                                </div>
                                <span className="text-[10px] text-theme-text-tertiary shrink-0">
                                  {pg.props.length}
                                </span>
                                <motion.div
                                  animate={{
                                    rotate: isPlayerExpanded ? 180 : 0,
                                  }}
                                  transition={{ duration: 0.15 }}
                                >
                                  <ChevronDown className="w-3.5 h-3.5 text-theme-text-tertiary shrink-0" />
                                </motion.div>
                              </button>

                              {/* Prop pills - only rendered when player is expanded */}
                              {isPlayerExpanded && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 px-3 pb-3">
                                  {pg.props.map((prop) => {
                                    const blocked = checkBlocked(prop);
                                    const ouLabel =
                                      prop.overUnder === 'Over'
                                        ? 'O'
                                        : prop.overUnder === 'Under'
                                          ? 'U'
                                          : '';
                                    const statLabel = formatPropType(prop.propType);

                                    // Best line lookup
                                    const mKey = `player_${prop.propType}`;
                                    const cKey = buildComparisonKey(
                                      prop.playerName || prop.team,
                                      mKey,
                                      typeof prop.line === 'number' ? prop.line : null,
                                      prop.overUnder,
                                    );
                                    const comparison = cKey ? oddsComparisons.get(cKey) : undefined;

                                    const PillElement = blocked.canAdd ? motion.button : 'button';
                                    const pillMotionProps = blocked.canAdd
                                      ? {
                                          whileHover: { scale: 1.03 },
                                          whileTap: { scale: 0.97 },
                                          transition: {
                                            type: 'spring' as const,
                                            stiffness: 400,
                                            damping: 25,
                                          },
                                        }
                                      : {};

                                    return (
                                      <PillElement
                                        type="button"
                                        key={prop.id}
                                        onClick={() => blocked.canAdd && handleAddProp(prop)}
                                        title={
                                          !blocked.canAdd
                                            ? blocked.reason
                                            : `Add ${[ouLabel, prop.line, statLabel].filter((part) => part != null && part !== '').join(' ')}`
                                        }
                                        className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg border text-xs transition ${
                                          !blocked.canAdd
                                            ? 'bg-theme-surface/20 border-theme-border/20 opacity-40 cursor-not-allowed'
                                            : 'bg-theme-surface/50 border-theme-border/30 hover:bg-theme-surface-hover hover:border-theme-border cursor-pointer'
                                        }`}
                                        {...pillMotionProps}
                                      >
                                        <span className="flex items-center gap-1.5 text-white font-medium">
                                          <span
                                            className={
                                              prop.overUnder === 'Over'
                                                ? 'text-green-400'
                                                : 'text-red-400'
                                            }
                                          >
                                            {ouLabel}
                                          </span>
                                          <span>{prop.line}</span>
                                          <span className="text-theme-text-tertiary font-normal">
                                            {statLabel}
                                          </span>
                                        </span>
                                        <span className="flex items-center gap-1.5">
                                          <span className="text-theme-text-secondary">
                                            {prop.odds}
                                          </span>
                                          {!oddsLocked && comparison && (
                                            <BestLineBadge
                                              comparison={comparison}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setComparisonModalData(comparison);
                                                setComparisonSourceProp(prop);
                                              }}
                                            />
                                          )}
                                          {blocked.canAdd && (
                                            <Plus className="w-3.5 h-3.5 text-theme-text-tertiary" />
                                          )}
                                        </span>
                                      </PillElement>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Game Props Card */}
                        {gameProps.length > 0 && (
                          <motion.div
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{
                              delay: playerGroups.length * 0.04,
                            }}
                            className="bg-theme-surface/30 border border-theme-border/20 rounded-xl p-3"
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm font-semibold text-white">Game Props</span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                              {gameProps.map((prop) => {
                                const blocked = checkBlocked(prop);

                                // Best line lookup
                                const cKey = buildComparisonKey(
                                  prop.playerName || prop.team,
                                  prop.propType,
                                  typeof prop.line === 'number' ? prop.line : null,
                                  prop.overUnder,
                                );
                                const comparison = cKey ? oddsComparisons.get(cKey) : undefined;

                                const PillElement = blocked.canAdd ? motion.button : 'button';
                                const pillMotionProps = blocked.canAdd
                                  ? {
                                      whileHover: { scale: 1.03 },
                                      whileTap: { scale: 0.97 },
                                      transition: {
                                        type: 'spring' as const,
                                        stiffness: 400,
                                        damping: 25,
                                      },
                                    }
                                  : {};

                                return (
                                  <PillElement
                                    type="button"
                                    key={prop.id}
                                    onClick={() => blocked.canAdd && handleAddProp(prop)}
                                    title={
                                      !blocked.canAdd
                                        ? blocked.reason
                                        : `Add ${getBetDisplayProp(prop)}`
                                    }
                                    className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg border text-xs transition ${
                                      !blocked.canAdd
                                        ? 'bg-theme-surface/20 border-theme-border/20 opacity-40 cursor-not-allowed'
                                        : 'bg-theme-surface/50 border-theme-border/30 hover:bg-theme-surface-hover hover:border-theme-border cursor-pointer'
                                    }`}
                                    {...pillMotionProps}
                                  >
                                    <span className="text-white font-medium">
                                      {getBetDisplayProp(prop)}
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                      <span className="text-theme-text-secondary">{prop.odds}</span>
                                      {!oddsLocked && comparison && (
                                        <BestLineBadge
                                          comparison={comparison}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setComparisonModalData(comparison);
                                            setComparisonSourceProp(prop);
                                          }}
                                        />
                                      )}
                                      {blocked.canAdd && (
                                        <Plus className="w-3.5 h-3.5 text-theme-text-tertiary" />
                                      )}
                                    </span>
                                  </PillElement>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          );
        })}

        {groupedProps.length === 0 && (
          <div className="text-center py-12 text-theme-text-tertiary">
            <p className="text-lg mb-2">No matching props</p>
            <p className="text-sm">Try adjusting your search or filters</p>
          </div>
        )}
      </div>
    );
  }

  /* Legacy Mode: Flat list layout */
  return (
    <div className="grid gap-2">
      {filteredProps.slice(0, 100).map((prop) => {
        const blocked = checkBlocked(prop);
        const isGameLevelProp = isTeamBet(prop.propType);

        const marketKey = isGameLevelProp ? prop.propType : `player_${prop.propType}`;
        const compKey = buildComparisonKey(
          prop.playerName || prop.team,
          marketKey,
          typeof prop.line === 'number' ? prop.line : null,
          prop.overUnder,
        );
        const propComparison = compKey ? oddsComparisons.get(compKey) : undefined;

        return (
          <div
            key={prop.id}
            className={`flex items-center justify-between p-3 rounded-lg border transition cursor-pointer ${
              !blocked.canAdd
                ? 'bg-gray-800/30 border-gray-600 opacity-50'
                : 'bg-gray-700/50 border-transparent hover:bg-gray-700 hover:border-gray-600'
            }`}
            onClick={() => blocked.canAdd && handleAddProp(prop)}
            title={!blocked.canAdd ? blocked.reason : 'Click to add'}
          >
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-white">{getBetDisplayName(prop, games)}</span>
                {isGameLevelProp && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-theme-primary/30 text-blue-400">
                    Game
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-400">{getBetDisplayProp(prop)}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-green-400 font-medium">{prop.odds}</span>
                {!oddsLocked && propComparison && (
                  <BestLineBadge
                    comparison={propComparison}
                    onClick={(e) => {
                      e.stopPropagation();
                      setComparisonModalData(propComparison);
                      setComparisonSourceProp(prop);
                    }}
                  />
                )}
              </div>
              {blocked.canAdd ? (
                <span className="text-gray-400 hover:text-green-400 transition">
                  <Plus className="w-5 h-5" />
                </span>
              ) : (
                <span className="text-yellow-500" title={blocked.reason}>
                  <AlertTriangle className="w-5 h-5" />
                </span>
              )}
            </div>
          </div>
        );
      })}

      {filteredProps.length > 100 && (
        <div className="text-center py-4 text-sm text-gray-500">
          Showing first 100 of {filteredProps.length} props. Use filters to narrow down.
        </div>
      )}
    </div>
  );
}
